import { startCheckoutRedirect } from '../services/stripe';
import { 
  getCheckoutState, 
  hasPendingCheckout,
} from './checkoutStorage';
import { isPasswordRecoveryPending } from './passwordRecoveryStorage';

const POST_AUTH_REDIRECT_KEY = 'leadsme:post_auth_redirect';

function readAuthUrlParams(): { search: URLSearchParams; hash: URLSearchParams } {
  const search = new URLSearchParams(window.location.search);
  const hashRaw = window.location.hash?.replace(/^#/, '') || '';
  const hash = new URLSearchParams(hashRaw);
  return { search, hash };
}

/**
 * OAuth/provider error already in the URL — do not wait for a PKCE exchange.
 */
export function getOAuthCallbackErrorFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const { search, hash } = readAuthUrlParams();
    const err =
      search.get('error_description') ||
      search.get('error') ||
      search.get('error_code') ||
      hash.get('error_description') ||
      hash.get('error') ||
      hash.get('error_code');
    return err && err.trim() ? err.trim() : null;
  } catch {
    return null;
  }
}

/**
 * True while a PKCE/code or implicit token handoff is still in the URL (not errors).
 * Protected routes must wait for exchange before bouncing to /login.
 */
export function hasOAuthExchangePendingInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (getOAuthCallbackErrorFromUrl()) return false;
    const { search, hash } = readAuthUrlParams();
    if (search.has('code')) return true;
    return hash.has('access_token') || hash.has('refresh_token');
  } catch {
    return false;
  }
}

/** @deprecated Prefer hasOAuthExchangePendingInUrl / getOAuthCallbackErrorFromUrl */
export function hasAuthCallbackInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (getOAuthCallbackErrorFromUrl()) return true;
    return hasOAuthExchangePendingInUrl();
  } catch {
    return false;
  }
}

/** Safe in-app path only (blocks open redirects). */
export function isSafeAppRedirect(path: string | null | undefined): path is string {
  if (!path || typeof path !== 'string') return false;
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  if (path.startsWith('/login') || path.startsWith('/signup')) return false;
  return true;
}

/** Persist deep link across Google OAuth (callback always lands on /app). */
export function stashPostAuthRedirect(path: string | null | undefined): void {
  if (!isSafeAppRedirect(path)) return;
  try {
    sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, path);
  } catch {
    // ignore
  }
}

export function clearPostAuthRedirect(): void {
  try {
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  } catch {
    // ignore
  }
}

export function consumePostAuthRedirect(): string | null {
  try {
    const path = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
    return isSafeAppRedirect(path) ? path : null;
  } catch {
    return null;
  }
}

/**
 * Standard SaaS: Handles all post-authentication redirect scenarios
 * 
 * This function centralizes all post-auth redirect logic to ensure consistency
 * across SignUpPage, LoginPage, and any other auth flows.
 * 
 * Supported scenarios:
 * 1. Login without payment → /app (or redirect param)
 * 2. Login with payment → Stripe checkout (or subscription management if already subscribed)
 * 3. Signup without payment → /app (or redirect param)
 * 4. Signup with payment → Stripe checkout (or subscription management if already subscribed)
 * 
 * Priority order:
 * 1. Pending checkout (from pricing page) → Check subscription → Stripe checkout or subscription management
 * 2. URL redirect param (?redirect=...) → specified URL
 * 3. Location state (from navigation) → specified URL
 * 4. Default → /app
 */
export const handlePostAuthRedirect = async (
  user: any,
  loading: boolean,
  searchParams: URLSearchParams,
  location: any,
  checkoutHandledRef: React.MutableRefObject<boolean>,
  showToast?: (message: string, variant?: 'success' | 'error' | 'warning' | 'info') => void,
  navigate?: (path: string, options?: { replace?: boolean }) => void,
  t?: (key: string) => string,
  setIsRedirecting?: (isRedirecting: boolean) => void
): Promise<boolean> => {
  // Don't redirect if still loading or user not authenticated
  if (loading || !user) {
    return false;
  }

  // Recovery OTP creates a session — stay on reset-password until the new password is set
  if (isPasswordRecoveryPending()) {
    return false;
  }

  const currentPath = window.location.pathname;

  // Don't redirect if on specific pages that handle their own logic
  if (currentPath === '/reset-password' || 
      currentPath === '/pricing' || 
      currentPath === '/billing/return') {
    return false;
  }

  // Standard SaaS: Don't redirect on auth pages unless there's a pending checkout
  const isAuthPage = currentPath === '/login' || currentPath === '/signup';
  if (isAuthPage && !hasPendingCheckout()) {
    return false;
  }

  // Handle pending checkout
  const { priceId, type } = getCheckoutState();
  
  if (priceId && type && !checkoutHandledRef.current) {
    checkoutHandledRef.current = true;
    
    // From here: we will actually go to Stripe. Now we can show the checkout loading state.
    if (setIsRedirecting) {
      setIsRedirecting(true);
    }

    try {
      await startCheckoutRedirect(priceId, type);
      return true;
    } catch (error) {
      // Other errors: redirect to pricing with error flag
      if (setIsRedirecting) {
        setIsRedirecting(false);
      }
      console.error('Error redirecting to checkout:', error);
      if (navigate) {
        navigate('/pricing?checkout=error', { replace: true });
      } else {
        window.location.href = '/pricing?checkout=error';
      }
      return true;
    }
  }

  // Standard redirect logic
  const redirectParam = searchParams.get('redirect');
  const fromState = (location.state as any)?.from?.pathname;
  const redirectTo = isSafeAppRedirect(redirectParam)
    ? redirectParam
    : isSafeAppRedirect(fromState)
      ? fromState
      : '/app';

  // Safety: avoid infinite loops
  if (redirectTo.startsWith('/login') || redirectTo.startsWith('/signup')) {
    window.location.href = '/app';
    return true;
  }
  
  if (navigate) {
    navigate(redirectTo, { replace: true });
  } else {
    window.location.href = redirectTo;
  }
  return true;
};
